import React, { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  Skeleton,
  Grid,
} from '@mui/material'
import {
  CalendarToday,
  WarningAmber,
  Autorenew,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { backendClient } from '../../../api/backend'

// ─── Single Monitoring Card ──────────────────────────────────────────────────

function MonitoringCard({ icon: Icon, label, count, subtitle, color, loading, onClick }) {
  const theme = useTheme()

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          background: theme.palette.background.paper,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: '8px' }} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        borderRadius: '12px',
        border: `1px solid ${alpha(color, 0.2)}`,
        background: alpha(color, 0.03),
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: `0 4px 16px ${alpha(color, 0.12)}`,
          borderColor: alpha(color, 0.35),
          transform: 'translateY(-1px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 20, color }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color,
                lineHeight: 1,
                mb: 0.25,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              {count}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 600,
                color: theme.palette.text.secondary,
                fontSize: '0.65rem',
              }}
            >
              {label}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.disabled,
              fontWeight: 500,
              fontSize: '0.68rem',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

// ─── Monitoring Cards Row ────────────────────────────────────────────────────

const MonitoringCards = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [summary, setSummary] = useState({ unreviewed: 0 })
  const [lifecycleCounts, setLifecycleCounts] = useState({ expiringSoon: 0, autoRenewing: 0 })

  useEffect(() => {
    async function fetchSummary() {
      setLoading(true)
      try {
        // backendClient.get returns the fetch Response — parse the bodies
        const [alertResponse, lifecycleResponse] = await Promise.all([
          backendClient.get('api/contract/monitoring/regulatory-alerts/summary').then((r) => r.json()),
          backendClient.get('api/contract/monitoring/lifecycle').then((r) => r.json()),
        ])
        setSummary(alertResponse)
        if (Array.isArray(lifecycleResponse)) {
          setLifecycleCounts({
            expiringSoon: lifecycleResponse.filter(
              (item) => item.lifecycle_status === 'expiring_soon'
            ).length,
            autoRenewing: lifecycleResponse.filter(
              (item) => item.auto_renewal === true
            ).length,
          })
        }
      } catch (err) {
        console.error('Error fetching monitoring summary:', err)
        setSummary({ unreviewed: 0 })
      } finally {
        setLoading(false)
      }
    }
    fetchSummary()
  }, [])

  const cards = [
    {
      icon: CalendarToday,
      label: t('dashboard.monitoring.expiringSoon'),
      count: lifecycleCounts.expiringSoon,
      subtitle: t('dashboard.monitoring.contractsExpiring'),
      color: theme.palette.warning.main,
      onClick: () => navigate('/monitoring'),
    },
    {
      icon: WarningAmber,
      label: t('dashboard.monitoring.regulatoryAlerts'),
      count: summary.unreviewed || 0,
      subtitle: t('dashboard.monitoring.unreviewedChanges'),
      color: theme.palette.error.main,
      onClick: () => navigate('/monitoring'),
    },
    {
      icon: Autorenew,
      label: t('dashboard.monitoring.autoRenewing'),
      count: lifecycleCounts.autoRenewing,
      subtitle: t('dashboard.monitoring.autoRenewContracts'),
      color: theme.palette.info.main,
      onClick: () => navigate('/contract'),
    },
  ]

  return (
    <Grid container spacing={2}>
      {cards.map((card, index) => (
        <Grid item xs={12} sm={4} key={index}>
          <MonitoringCard
            icon={card.icon}
            label={card.label}
            count={card.count}
            subtitle={card.subtitle}
            color={card.color}
            loading={loading}
            onClick={card.onClick}
          />
        </Grid>
      ))}
    </Grid>
  )
}

export default MonitoringCards
